<template>
    <wrapper-features-templates-two-column
    :showTopFullSlot="showTopFullSlot || showEmptyState"
    :showLeftColumnSlot="showColumnSlot"
    :showRightColumnSlot="showColumnSlot"
    :showFullViewLoader="showFullViewLoader"
    :applyErrorState="applyErrorState"
    :customHeaderTabs="customHeaderTabs"
    :showTopRightAreaSlot="getAllPatrimonyInfo && !showFullViewLoader && !applyErrorState"
    @customTabClicked="tabClickedHandler"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Header Dropdowns -->
        <template #accounts-card-slot-area>
            <!-- Currency & Product Picker -->
            <currency-and-product-picker
            v-if="getAllPatrimonyInfo && !showFullViewLoader && !applyErrorState"
            :selectedCurrency="selectedCurrency"
            :selectedProduct="selectedProduct"
            :selectedTab="selectedTab"
            :isMobile="getMobileAppState"
            @updateSelectedCurrency="[selectedCurrency = $event, savedBackState.currencySelected = $event]"
            @updateSelectedProduct="[selectedProduct = $event, savedBackState.productSelected = $event]"
            @updateSelectedTab="tabClickedHandler"/>
        </template>
        <!-- Download Excel Button Desktop -->
        <template #tabs-slot-extra-content>
            <button-secondary
            v-if="getAllPatrimonyInfo && !showFullViewLoader && !applyErrorState && !getMobileAppState"
            :label="t('accounts.patrimony.labels.excel')"
            icon="download"
            buttonSize="small"
            @clicked="downloadLink = createPatrimonyExcelUrl()" />
        </template>
        <!-- Download Excel Button Mobile -->
        <template v-slot:top-right-button-mobile-slot>
            <button-tertiary
            v-if="getAllPatrimonyInfo && !showFullViewLoader && !applyErrorState && getMobileAppState"
            class="top-right-mobile-button"
            buttonStyle="round"
            buttonSize="small"
            icon="download"
            :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }"
            @clicked="downloadLink = createPatrimonyExcelUrl()"/>
        </template>
        <!-- Top Full Wrapper -->
        <template v-slot:top-full-wrapper>
            <!-- Full Content Chart -->
            <transition-group name="common-enter-animation" mode="out-in" appear>
                <div v-if="showTopFullSlot" class="full-content" :class="{ mobile: getMobileAppState }">
                    <component
                    :is="chartComponent"
                    :key="`${selectedTab}${selectedProduct}`"
                    :selectedCurrency="selectedCurrency"
                    :selectedProduct="selectedProduct"
                    :savedBackState="savedBackState"
                    :selectedTab="selectedTab"
                    @updateSelectedTab="tabClickedHandler"
                    @productChanged="[selectedProduct = $event, savedBackState.productSelected = $event]"
                    @resetPickerDrops="resetPickerDropsHandler"
                    @showEmptyState="showEmptyState = true" />
                </div>
                <!-- Empty-State -->
                <empty-state-generic
                v-if="showEmptyState"
                class="patrimony-empty-state"
                illustration="emptyState/patrimony"
                :key="`emptyState${selectedTab}`"
                :title="emptyStateTitle"
                :description="emptyStateDescription"/>
            </transition-group>
        </template>
        <!-- Left Column -->
        <template v-slot:left-column>
            <transition name="common-enter-animation" mode="out-in" appear>
                <div
                v-if="showColumnSlot" :key="`${selectedTab}${selectedProduct}`"
                class="left-column-content"
                :class="{ mobile: getMobileAppState }">
                    <component
                    :is="chartComponent"
                    :selectedCurrency="selectedCurrency"
                    :selectedProduct="selectedProduct"
                    :savedBackState="savedBackState"
                    :selectedTab="selectedTab"
                    @updateSelectedTab="tabClickedHandler"
                    @productChanged="[selectedProduct = $event, savedBackState.productSelected = $event]"
                    @resetPickerDrops="resetPickerDropsHandler"
                    @showEmptyState="showEmptyState = true" />
                </div>
            </transition>
        </template>
        <!-- Right Column -->
        <template v-slot:right-column>
            <transition name="common-enter-animation" mode="out-in" appear>
                <div v-if="showColumnSlot" :key="`${selectedTab}${selectedProduct}`" class="right-column-content">
                    <patrimony-table
                    :selectedCurrency="selectedCurrency"
                    :savedBackState="savedBackState"
                    :selectedTab="selectedTab"
                    @updateSelectedTab="tabClickedHandler" />
                </div>
            </transition>
        </template>
	</wrapper-features-templates-two-column>
    <!-- Component to handle download logic-->
	<download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesTwoColumn from '@/components/wrappers/featuresTemplates/TwoColumn'
import CurrencyAndProductPicker from '@/components/features/accounts/patrimony/CurrencyAndProductPicker'
import PatrimonyTable from '@/components/features/accounts/patrimony/table/Base'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'
/* Vue */
import { ref, computed, defineAsyncComponent, watch } from 'vue'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* Assets & Helpers */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { createPatrimonyExcelUrl } from '@/network/api/home.api.js'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions */
const { fetchPatrimony, savePatrimonyBackState } = mapActions(moduleMapper.ACCOUNTS_PATRIMONY)
const { getAllPatrimonyInfo, getPatrimonyBackState, getAccountsCurrenciesList } = mapGetters(moduleMapper.ACCOUNTS_PATRIMONY)
const { getMobileAppState, getDeviceInUse } = mapGetters()

/* Data */
const showFullViewLoader = ref(false)
const applyErrorState = ref(false)
const selectedCurrency = ref('')
const selectedProduct = ref('')
const downloadLink = ref(null)

const resetPickerDropsHandler = () => {
    selectedCurrency.value = getAccountsCurrenciesList.value.length > 1 ? 'allCurrencies' : getAccountsCurrenciesList.value[0].name
}

/*************************************************
*                                                *
*                   Empty State                  *
*                                                *
*************************************************/
const showEmptyState = ref(false)

const emptyStateTitle = computed(() => {
    return selectedTab.value === 'assets' ? t('accounts.patrimony.emptyStates.emptyTitleAssets') : t('accounts.patrimony.emptyStates.emptyTitlePassives')
})
const emptyStateDescription = computed(() => {
    return selectedTab.value === 'assets' ? t('accounts.patrimony.emptyStates.emptyTextAssets') : t('accounts.patrimony.emptyStates.emptyTextPassives')
})

/*************************************************
*                                                *
*               Slots & Components               *
*                                                *
*************************************************/
const showTopFullSlot = computed(() => {
    return (selectedTab.value === 'assetsVsLiabilities' || (selectedCurrency.value && selectedProduct.value !== 'allProducts')) && !showFullViewLoader.value && !applyErrorState.value && !showEmptyState.value
})

const showColumnSlot = computed(() => {
    return selectedTab.value !== 'assetsVsLiabilities' && selectedCurrency.value && selectedProduct.value === 'allProducts' && !showFullViewLoader.value && !applyErrorState.value && !showEmptyState.value
})

const chartComponent = computed(() => {
    const folder = getMobileAppState.value ? 'mobile' : 'desktop'

    return defineAsyncComponent(() => import('@/components/features/accounts/patrimony/chart/' + folder + '/Base'))
})

/*************************************************
*                                                *
*                   Custom Tabs                  *
*                                                *
*************************************************/
const customHeaderTabs = ref([
    {
        id: 'assets',
        label: t('accounts.patrimony.tabs.assets'),
        selected: true,
        disabled: false
    },
    {
        id: 'liabilities',
        label: t('accounts.patrimony.tabs.liabilities'),
        selected: false,
        disabled: false
    },
    {
        id: 'assetsVsLiabilities',
        label: t('accounts.patrimony.tabs.assetsVsLiabilities'),
        selected: false,
        disabled: false
    }
])

const selectedTab = computed(() => {
    return customHeaderTabs.value.find(obj => obj.selected).id
})

const tabClickedHandler = event => {
    customHeaderTabs.value.forEach(tab => {
		tab.selected = tab.id === event
	})

    savedBackState.value.tabSelected = event
}

watch(() => selectedProduct.value, () => {
    customHeaderTabs.value.forEach(tab => {
        tab.disabled = selectedProduct.value !== 'allProducts'
    })
})

watch(() => selectedTab.value, () => {
    showEmptyState.value = false
})

watch(() => selectedCurrency.value, () => {
    showEmptyState.value = false
})

/*************************************************
*                                                *
*                    Requests                    *
*                                                *
*************************************************/
/* Data helpers for back state */
const applyBackState = ref(false)
const savedBackState = ref({
    graphicViewSelected: false,
    currencySelected: null,
    productSelected: null,
    tabSelected: null
})

const requestPatrimony = async () => {
    showFullViewLoader.value = true
    showFeedbackToast.value = false
    applyErrorState.value = false

    try {
        await fetchPatrimony()
        /* if we get here from back, we need to send the user to the previous state */
        if (getPatrimonyBackState.value) {
            savedBackState.value = getPatrimonyBackState.value
            selectedCurrency.value = savedBackState.value.currencySelected
            selectedProduct.value = savedBackState.value.productSelected
            savePatrimonyBackState(null)
        }
        showFullViewLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, requestPatrimony)
        applyErrorState.value = true
    }
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showFullViewLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestPatrimony()
</script>

<style lang="scss" scoped>
.left-column-content,
.right-column-content,
.full-content,
.empty-state-wrapper {
    @include commonEnteringAnimation;

    width: 100%;
}

.left-column-content {
    @include cardGeneral1;

    padding: 16px 0;

    &.mobile {
        padding: 0;

        background-color: transparent;
    }
}

.full-content,
.patrimony-empty-state {
    @include cardGeneral1;

    align-items: center;
}

.top-right-mobile-button {
    position: absolute;
    top: 16px;
    right: 16px;

    z-index: 3;

    &.mobile-status-bar-android {
        top: calc(16px + var(--android-top-padding));
    }

    &.mobile-status-bar-ios {
        top: calc(16px + constant(safe-area-inset-top));
        top: calc(16px + env(safe-area-inset-top));
    }
}

.full-content {
    :deep(.chart-content-wrapper) {
        display: flex;
        flex-direction: column;
        align-items: center;

        padding: 0;

        .chart-wrapper {
            margin-bottom: 8px;

            .complex-chart-wrapper {
                display: flex;
                justify-content: center;
            }
        }

        .graph-labels {
            display: unset;

            width: auto;
            max-width: 100%;

            column-count: 2;

            padding: 0;

            .label {
                width: auto;

                padding-top: 16px;
            }
        }
    }

    &.mobile {
        background-color: transparent;
        padding: 16px 0;
    }
}
</style>
